import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { PageHeader } from '../../components/PageHeader';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { useUpdatePortalBranding } from './brandingQueries';
import { usePortalSettings } from './queries';

/**
 * Das Erscheinungsbild des Portals einrichten -- F4-21, K-103.
 *
 * Diese Seite gehoert zur **Agentenoberflaeche** und nicht zum Portal: Sie
 * wird von der Administration des Mandanten bedient. Sie liegt trotzdem in
 * `features/portal`, weil sie dieselbe Antwort liest wie das Portal -- und
 * zwei Module fuer dasselbe Objekt liefen auseinander.
 *
 * **Kein HTML im Begruessungstext.** Er erscheint im Browser jedes
 * Portalnutzers dieses Mandanten; als HTML waere er eine Flaeche fuer alles
 * Weitere. Der Server weist ihn ohnehin nur als Nur-Text an.
 */
export function BrandingPage() {
  const { t } = useTranslation();
  const settings = usePortalSettings();
  const update = useUpdatePortalBranding();

  const [loaded, setLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [color, setColor] = useState('#0176d3');
  const [greeting, setGreeting] = useState('');

  // Anpassung waehrend des Rendervorgangs statt eines Effekts -- siehe
  // `features/knowledge/ArticleEditor.tsx`.
  if (settings.data && !loaded) {
    setLoaded(true);
    setLogoUrl(settings.data.logo_url ?? '');
    setColor(settings.data.primary_color ?? '#0176d3');
    setGreeting(settings.data.greeting ?? '');
  }

  if (settings.isPending) return <LoadingIndicator />;
  if (settings.isError) return <ErrorNotice error={settings.error} />;

  return (
    <>
      <PageHeader title={t('portal.branding')} subtitle={t('portal.brandingHint')} />

      <section className="card">
        <div className="field">
          <label className="field__label" htmlFor="branding-logo">
            {t('portal.logoUrl')}
          </label>
          <input
            id="branding-logo"
            className="field__control"
            value={logoUrl}
            placeholder="https://…"
            onChange={(event) => {
              setLogoUrl(event.target.value);
            }}
          />
          <span className="field__hint">{t('portal.logoUrlHint')}</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="branding-color">
            {t('portal.primaryColor')}
          </label>
          <input
            id="branding-color"
            type="color"
            className="field__control"
            value={color}
            onChange={(event) => {
              setColor(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="branding-greeting">
            {t('portal.greeting')}
          </label>
          <textarea
            id="branding-greeting"
            className="field__control field__control--area"
            rows={4}
            value={greeting}
            onChange={(event) => {
              setGreeting(event.target.value);
            }}
          />
        </div>

        {update.isError ? <ErrorNotice error={update.error} /> : null}

        <Button
          variant="primary"
          busy={update.isPending}
          onClick={() => {
            update.mutate({
              logo_url: logoUrl.trim(),
              primary_color: color,
              greeting: greeting.trim(),
            });
          }}
        >
          {t('app.save')}
        </Button>
      </section>
    </>
  );
}
